import React from 'react';
import { Row, Col, Popover, Tooltip, Popconfirm } from 'antd';
import {
  AssignButton,
  CommentButton,
  JobLink,
  CommentDivTitleStyle,
  PopoverGlobalStyle,
  StyledCommentContent
} from './styledComponents';
import { ActionButton } from '../../CustomComponents/Table/styledComponents';
import CommentIcon from '../../../Assets/Icons/CommentIcon';
import { DeleteOutlined, BulbOutlined } from '@ant-design/icons';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';

/**
 * Multi-select filter: filterValue is an array of selected display strings.
 * Works for all data columns; converts raw value to string before comparison.
 */
const multiSelectFilter = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const val = row.getValue(columnId);
  const strVal = val != null ? String(val) : 'N/A';
  return filterValue.includes(strVal);
};

/**
 * Returns the handler that opens a job for the given tab, or null when the
 * tab has no open action (Job ID / Job Number then render as plain text).
 */
export const getOpenJobHandler = ({
  activeKey,
  startAssignedJobWithNewEditor,
  resumeJobWithNewEditor,
  viewJob
}) => {
  if (activeKey === '4') return startAssignedJobWithNewEditor;
  if (activeKey === '0') return resumeJobWithNewEditor;
  if (activeKey === '1' || activeKey === '2' || activeKey === '3') return viewJob;
  return null;
};

/**
 * Create column definitions for TanStack Table
 * All cell renderers migrated from tableConfig.js
 */
export const createColumns = ({
  activeKey,
  activeTab1,
  startAssignedJobWithNewEditor,
  resumeJobWithNewEditor,
  viewJob,
  releaseJobHandler,
  disableUser
}) => {
  const openJob = getOpenJobHandler({
    activeKey,
    startAssignedJobWithNewEditor,
    resumeJobWithNewEditor,
    viewJob
  });

  const renderJobLink = ({ getValue, row }) => {
    const val = getValue();
    if (!val) return 'N/A';
    if (!openJob) return val;
    return (
      <JobLink
        type='button'
        onClick={e => {
          e.stopPropagation();
          openJob(row.original.jobId);
        }}
      >
        {val}
      </JobLink>
    );
  };

  const baseColumns = [
    // Job ID Column
    {
      id: 'jobId',
      accessorKey: 'jobNumber',
      header: 'Job ID',
      size: 100,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: renderJobLink
    },

    // Author Column (text truncation with tooltip)
    {
      id: 'author',
      accessorKey: 'author',
      header: 'Author',
      size: 150,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },

    // Primary Care Physician Column (conditional - only for My Jobs / In Progress tab)
    {
      id: 'primaryCarePhysician',
      accessorKey: 'primaryCarePhysician',
      header: 'Primary Care Physician',
      size: 210,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },

    // Patient Name Column (text truncation with tooltip)
    {
      id: 'patientName',
      accessorKey: 'patientName',
      header: 'Patient Name',
      size: 210,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },

    // Job Number Column
    {
      id: 'jobNumber',
      accessorKey: 'jobNumber',
      header: 'Job Number',
      size: 120,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: renderJobLink
    },

    // Work Type Column
    {
      id: 'workType',
      accessorKey: 'workType',
      header: 'Work Type',
      size: 180,
      minSize: 120,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue() || 'N/A'
    },

    // Dictation Template Column
    {
      id: 'dictationTemplate',
      accessorFn: row => row.dictationTemplate?.templateName,
      header: 'Dictation Template',
      size: 240,
      minSize: 120,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return val;
      }
    },

    // Biller Column
    {
      id: 'biller',
      accessorFn: row => row.biller?.fullName,
      header: 'Biller',
      size: 150,
      minSize: 100,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },

    // Submitted Time Column — accessor returns formatted string for faceted filter;
    // sortingFn uses raw date for correct chronological ordering.
    {
      id: 'submissionTime',
      accessorFn: row => {
        if (!row.submissionTime) return 'N/A';
        return formatDateTime(row.submissionTime, 'N/A');
      },
      header: 'Submitted Time',
      size: 180,
      minSize: 120,
      maxSize: 500,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.submissionTime;
        const b = rowB.original.submissionTime;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      },
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue()
    },

    // Effective Date Column — same pattern as submissionTime.
    {
      id: 'effectiveDate',
      accessorFn: row => {
        if (!row.effectiveDate) return 'N/A';
        return formatDate(row.effectiveDate, 'N/A');
      },
      header: 'Encounter Date',
      size: 180,
      minSize: 120,
      maxSize: 500,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.effectiveDate;
        const b = rowB.original.effectiveDate;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      },
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue()
    },

    // Facility Column
    {
      id: 'facilityName',
      accessorFn: row => row.facility?.facilityName,
      header: 'Facility',
      size: 180,
      minSize: 120,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <Popover placement='bottom' content={<h5>{val}</h5>}>
            <span title={val}>{val}</span>
          </Popover>
        );
      }
    },

    // Current level of review Column — accessor returns display string so
    // faceted values appear as "Level 1" / "Level 2" in the checkbox list.
    {
      id: 'currentLevel',
      accessorFn: row => {
        const val = row.currentLevelOfReview;
        return val === 'LEVEL_1'
          ? 'Level 1'
          : val === 'LEVEL_2'
          ? 'Level 2'
          : 'N/A';
      },
      header: 'Current level of review',
      size: 180,
      minSize: 120,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        return <span title={val}>{val}</span>;
      }
    },

    // Current Assignee Column (conditional - only for In-Review tab)
    {
      id: 'assignedTo',
      accessorFn: row => row.reviewerInProgress?.fullName,
      header: 'Current Assignee',
      size: 150,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },

    // Bill Status Column — accessor returns display string for checkbox list.
    {
      id: 'billStatus',
      accessorFn: row => {
        const val = row.billStatus;
        return val !== null && val !== undefined
          ? val
            ? 'Billed'
            : 'Not Billed'
          : 'N/A';
      },
      header: 'Bill Status',
      size: 120,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue()
    },

    // Submission to Reviewer Level 1 Time Column (conditional - only for In Progress tab)
    {
      id: 'submissionToReviewerLevel1Time',
      accessorFn: row => {
        if (!row.submissionToReviewerLevel1Time) return 'N/A';
        return formatDateTime(row.submissionToReviewerLevel1Time);
      },
      header: 'Submission Time to Reviewer',
      size: 280,
      minSize: 150,
      maxSize: 500,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.submissionToReviewerLevel1Time;
        const b = rowB.original.submissionToReviewerLevel1Time;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      },
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue()
    },

    // EMR Submission Time Column (conditional - only for In Progress tab)
    {
      id: 'emrSubmissionTime',
      accessorFn: row => {
        if (!row.emrSubmissionTime) return 'N/A';
        return formatDateTime(row.emrSubmissionTime);
      },
      header: 'EMR Submission Time',
      size: 220,
      minSize: 120,
      maxSize: 500,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.emrSubmissionTime;
        const b = rowB.original.emrSubmissionTime;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      },
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue()
    },

    // Reason For Failing Column (conditional - only for Incomplete tab)
    {
      id: 'reasonForFailing',
      accessorKey: 'reasonForFailing',
      header: 'Reason For Failing',
      size: 200,
      minSize: 150,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 30 ? val.slice(0, 30) + '...' : val}
          </span>
        );
      }
    },

    // Scribe Column (conditional - only for In Progress tab, activeKey === '0')
    {
      id: 'scribe',
      accessorFn: row => row.scribe?.fullName,
      header: 'Scribe',
      size: 150,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },

    // Admission Date Column
    {
      id: 'admissionDate',
      accessorFn: row => {
        const date = row.patientDemographics?.admissionDate;
        if (!date) return 'N/A';
        return formatDate(date);
      },
      header: 'Admission Date',
      size: 160,
      minSize: 100,
      maxSize: 500,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.patientDemographics?.admissionDate;
        const b = rowB.original.patientDemographics?.admissionDate;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      },
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue()
    },

    // MRN Column
    {
      id: 'mrn',
      accessorFn: row => row.patientDemographics?.medicalRecordNumber,
      header: 'MRN',
      size: 150,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue() || 'N/A'
    },

    // Discharge Date Column
    {
      id: 'dischargeDate',
      accessorFn: row => {
        const date = row.patientDemographics?.dischargeDate;
        if (!date) return 'N/A';
        return formatDate(date);
      },
      header: 'Discharge Date',
      size: 160,
      minSize: 100,
      maxSize: 500,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.patientDemographics?.dischargeDate;
        const b = rowB.original.patientDemographics?.dischargeDate;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      },
      filterFn: multiSelectFilter,
      cell: ({ getValue }) => getValue()
    },

    // Delete Column (conditional icon button with popconfirm)
    {
      id: 'delete',
      header: '',
      size: 130,
      minSize: 80,
      maxSize: 130,
      enableResizing: false,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const rowData = row.original;
        const userData = JSON.parse(localStorage.getItem('user'));
        const showDelete =
          userData?.userType !== 'scribe' &&
          activeKey !== '4' &&
          activeKey !== '2' &&
          (activeKey !== '1' || !rowData?.reviewerInProgress?.fullName);

        const latestComment = rowData.comments?.[0];
        const showComment =
          activeKey === '0' && latestComment?.comment?.length > 0;
        const showReason = activeKey === '3' && rowData.reasonForFailing;

        return (
          <Row align='middle' style={{ gap: 4, flexWrap: 'nowrap' }}>
            {showComment && (
              <Col>
                <Popover
                  title={
                    <CommentDivTitleStyle>
                      {`${
                        latestComment.userType[0].toUpperCase() +
                        latestComment.userType.substring(1).toLowerCase()
                      } Comment`}
                    </CommentDivTitleStyle>
                  }
                  content={
                    <StyledCommentContent>
                      {latestComment.comment}
                    </StyledCommentContent>
                  }
                >
                  <PopoverGlobalStyle />
                  <CommentButton icon={<CommentIcon />} shape='circle' />
                </Popover>
              </Col>
            )}
            {showReason && (
              <Col>
                <Popover
                  title={<CommentDivTitleStyle>Reason</CommentDivTitleStyle>}
                  content={<div>{rowData.reasonForFailing}</div>}
                >
                  <PopoverGlobalStyle />
                  <CommentButton icon={<CommentIcon />} shape='circle' />
                </Popover>
              </Col>
            )}
            {showDelete && (
              <Col>
                <Tooltip title={rowData.isEnabled ? 'Disable Job' : 'Delete Job'}>
                  <Popconfirm
                    title='Delete this job?'
                    onConfirm={() => {
                      disableUser(rowData.jobId);
                    }}
                    okText='Yes'
                    cancelText='No'
                    overlayClassName='popover-delete-confirm'
                  >
                    <style>
                      {`
                        .popover-delete-confirm .ant-popover-inner-content {
                          padding: 16px !important;
                        }
                      `}
                    </style>
                    <ActionButton icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Tooltip>
              </Col>
            )}
            {rowData.hasAiContentToAdd && (
              <Tooltip title='AI content ready'>
                <BulbOutlined style={{ color: '#47a4ea', fontSize: 16, cursor: 'default', marginLeft: 20 }} />
              </Tooltip>
            )}
          </Row>
        );
      }
    },

    // Release Column (conditional - only for activeKey === '7')
    {
      id: 'release',
      header: '',
      size: 100,
      minSize: 80,
      enableResizing: false,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const rowData = row.original;
        return activeKey === '7' ? (
          <AssignButton
            type='secondary'
            onClick={() => releaseJobHandler(rowData.jobId)}
          >
            Release
          </AssignButton>
        ) : null;
      }
    }
  ];

  // Filter conditional columns based on activeKey and activeTab1
  return baseColumns.filter(col => {
    // Always include these columns
    if (
      col.id === 'jobId' ||
      col.id === 'author' ||
      col.id === 'patientName' ||
      col.id === 'jobNumber' ||
      col.id === 'workType' ||
      col.id === 'dictationTemplate' ||
      col.id === 'biller' ||
      col.id === 'submissionTime' ||
      col.id === 'effectiveDate' ||
      col.id === 'facilityName' ||
      col.id === 'admissionDate' ||
      col.id === 'mrn' ||
      col.id === 'dischargeDate' ||
      col.id === 'delete' ||
      col.id === 'release'
    ) {
      return true;
    }

    // Current Level - for In-Review, Ready, and In progress tabs (activeKey === '1', '4', or '5')
    if (col.id === 'currentLevel') {
      return activeKey === '1' || activeKey === '4' || activeKey === '5';
    }

    // Assigned To - for In-Review, Ready, and In progress tabs (activeKey === '1', '4', or '5')
    if (col.id === 'assignedTo') {
      return activeKey === '1' || activeKey === '4' || activeKey === '5';
    }

    // Bill Status - only for Completed tab (activeKey === '2')
    if (col.id === 'billStatus') {
      return activeKey === '2';
    }

    // Submission to Reviewer L1 Time / EMR Submission Time - all tabs
    if (
      col.id === 'submissionToReviewerLevel1Time' ||
      col.id === 'emrSubmissionTime'
    ) {
      return true;
    }

    // Reason For Failing - only for Incomplete tab (activeKey === '3')
    if (col.id === 'reasonForFailing') {
      return activeKey === '3';
    }

    // Scribe - for My Jobs tabs and Jobs to Review / Ready (activeKey === '0', '1', '2', '3', '4')
    if (col.id === 'scribe') {
      return activeKey === '0' || activeKey === '1' || activeKey === '2' || activeKey === '3' || activeKey === '4';
    }

    return true;
  });
};
